import { Box } from '@mui/system';
import { sentenceCase } from 'change-case';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
// material
import {
  Card,
  Checkbox,
  Container,
  IconButton,
  Stack,
  tableCellClasses,
  Typography,
  Select,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Label from '../../components/Label';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import { fDate, fDateTime2 } from '../../utils/formatTime';
import DisburseToolbar from './DisburseToolbar';
import { NOT_AVAILABLE } from '../Global/Constants';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getDisburseList } from '../../store/Slice/Disburse/DisburseSlice';
import useDisburseSelector from '../../store/Slice/Disburse/DisburseSelector';
import { isValidValue, normalizeValue } from '../../utils/CommonFunction';
import useDebounce from '../../utils/useDebounce';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'id', label: 'APPLICATION ID', alignRight: false },
  { id: 'lanId', label: 'LAN ID', alignRight: false },
  { id: 'created', label: 'SUBMISSION DATE', alignRight: false },
  { id: 'name', label: 'NAME', alignRight: false },
  { id: 'companyName', label: 'COMPANY NAME', alignRight: false },
  { id: 'contactNumber', label: 'CONTACT NUMBER', alignRight: false },
  { id: 'sourceFlag', label: 'FILE SOURCE', alignRight: false },
  { id: 'loanType', label: 'PRODUCT', alignRight: false },
  { id: 'city', label: 'CITY', alignRight: false },
  { id: 'amount', label: 'AMOUNT', alignRight: false },
  { id: 'bankName', label: 'BANK', alignRight: false },
  { id: 'dsaName', label: 'DSA NAME', alignRight: false },
  { id: 'status', label: 'STATUS', alignRight: false },
  { id: 'lastUpdate', label: 'LAST UPDATE', alignRight: false },
  { id: 'action', label: 'ACTION', alignRight: false, align: 'center' }
];

// ----------------------------------------------------------------------

const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: '#fff',
  '&:nth-of-type(even)': {
    backgroundColor: '#fafbfc'
  },
  '&:hover': {
    backgroundColor: '#f1f5f9 !important'
  },
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    color: '#64748b',
    textTransform: 'uppercase',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155'
  }
}));

function Disburse() {
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState();
  const [filterName, setFilterName] = useState('');
  const [filterState, setFilterState] = useState({
    bankId: null,
    loanTypeId: null,
    cityId: null,
    city: null,
    statusName: ' ',
    statusId: null
  });
  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const debouncedSearch = useDebounce(filterName, 600);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { DisburseListData } = useDisburseSelector();

  useEffect(() => {
    const selectedItem = localStorage.getItem('selectedItem');
    if (selectedItem !== null) {
      localStorage.removeItem('selectedItem');
    }
  }, []);

  const getDisburseListApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      stageId: 58,
      filterModel: {
        bankId: filterState.bankId || null,
        loanTypeId: filterState.loanTypeId || null,
        cityId: filterState.cityId || null,
        disburseStatusName: filterState.statusId ? filterState.statusName : null
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: order || 'desc'
      }
    };
    dispatch(getDisburseList(credentials));
  }, [LoginData, filterState, debouncedSearch, order, dispatch, page, rowsPerPage]);

  useEffect(() => {
    getDisburseListApi();
  }, [getDisburseListApi]);

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };
  const handleRequestSort = (event, property) => {
    if (property !== 'action') {
      setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const list = DisburseListData.filter(
        (item, index) =>
          item.status?.toLowerCase() === 'disbursed' || item.status?.toLowerCase() === 'u-disbursed'
      );
      const newSelecteds = list.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, item) => {
    const selectedIndex = selected.indexOf(item);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, item);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  useEffect(() => {
    if (!DisburseListData) {
      setHasNextPage(true);
      return;
    }
    // If API returned fewer rows → last page
    setHasNextPage(DisburseListData.length >= rowsPerPage);
  }, [DisburseListData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  return (
    <Page title="Partner Management System || Disbursed Applications">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title, and Breadcrumb */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <PaymentsOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Disbursed Applications
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Track and manage loan disbursement records and details.
              </Typography>
            </Box>
          </Stack>

          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <DisburseToolbar
            numSelected={selected.length}
            filterName={filterName}
            setFilterName={setFilterName}
            onFilterName={handleFilterByName}
            filterDatesRange={filterDatesRange}
            setFilterDatesRange={setFilterDatesRange}
            filterState={filterState}
            setFilterState={setFilterState}
            parent="disburse"
            selected={selected}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, minHeight: 380, overflow: 'auto' }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={DisburseListData?.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  isCheckBox="true"
                />
                <TableBody>
                  {DisburseListData?.map((item, index) => {
                    const isItemSelected = selected.indexOf(item) !== -1;
                    return (
                      <StyledTableRow
                        hover
                        key={item.applicationId || index}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                      >
                        <TableCell padding="checkbox" sx={{ pl: 2.5 }}>
                          <Checkbox
                            className={
                              item.status?.toLowerCase() === 'disbursed' ||
                              item.status?.toLowerCase() === 'u-disbursed'
                                ? 'enabledDocument'
                                : 'disabledDocument'
                            }
                            checked={isItemSelected}
                            onChange={(event) => handleClick(event, item)}
                            sx={{
                              color: '#cbd5e1',
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                            }}
                          />
                        </TableCell>
                        <StyledTableCell align="left">
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: '#0284c7',
                              fontSize: '13px'
                            }}
                          >
                            {normalizeValue(item?.applicationId, NOT_AVAILABLE)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.lanId, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(
                            item?.submissionDate ? fDate(item.submissionDate) : null,
                            NOT_AVAILABLE
                          )}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ fontWeight: 600, color: '#0f172a' }}>
                          {normalizeValue(item?.customerName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.companyName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.contactNumber, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <Label
                            variant="ghost"
                            color={
                              item?.sourceFlag === 'LOGIN_FILE' ||
                              item?.logInStatusName === 'Rejected'
                                ? 'warning'
                                : 'primary'
                            }
                            sx={{
                              opacity: 0.8,
                              fontWeight: 500
                            }}
                          >
                            {normalizeValue(item?.sourceFlag, NOT_AVAILABLE)}
                          </Label>
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.loanType, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.cityName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#0f172a', fontWeight: 600 }}>
                          {normalizeValue(item?.loanAmount, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.bankName, NOT_AVAILABLE)}
                        </StyledTableCell>

                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.dsaName, NOT_AVAILABLE)}
                        </StyledTableCell>

                        <StyledTableCell align="left">
                          <Label
                            variant="ghost"
                            color={
                              item?.status === 'Closed' || item?.status === 'Rejected'
                                ? 'warning'
                                : 'success'
                            }
                          >
                            {isValidValue(item?.status)
                              ? sentenceCase(item?.status)
                              : NOT_AVAILABLE}
                          </Label>
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {isValidValue(item?.lastUpdatedDate)
                            ? fDate(item.lastUpdatedDate)
                            : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <UserMoreMenu rowId={item} parent="disburse" />
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {DisburseListData?.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <StyledTableCell align="center" colSpan={15} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Modern Pagination Footer */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2, px: 3, borderTop: '1px solid #f1f5f9', bgcolor: '#ffffff', gap: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Showing</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                {DisburseListData?.length || 0} records
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                Page <span style={{ fontWeight: 600, color: '#0f172a' }}>{page + 1}</span>
              </Typography>

              <Stack direction="row" spacing={1}>
                <IconButton
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  disabled={!hasNextPage}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </Card>
      </Container>
    </Page>
  );
}
export default Disburse;

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ paddingTop: '1rem' }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};
